<template>
  <div>
    <QPullToRefresh no-mouse @refresh="loadRecruitments">
      <transition appear enter-active-class="animated slideInLeft" leave-active-class="animated slideOutRight">
        <QPage padding>
          <SurveyTodoComponent v-if="surveyTodo.hasTodo()" />
          <Demands :demands="recruitments" />
          <NoRecruitment
            v-if="!loading && recruitments.length === 0"
            :title="$t('ETAB.PAGES.DEMAND_DASHBOARD.@NO_IN_PROGRESS_DEMAND')"
            :img="inProgressImg"
            :btn-label="$t('ETAB.BUTTONS.@CREATE_DEMAND')"
            :btn-action="goToCreation"
          />
        </QPage>
      </transition>
      <QPageSticky class="sticky-over-layout" position="bottom-right" :offset="[10, 8]">
        <QBtn :loading="loading" fab icon="icon-f-rotate-cw" color="secondary" @click="loadRecruitments" />
      </QPageSticky>
    </QPullToRefresh>
  </div>
</template>

<script setup>
  import inProgressImg from "@etab/assets/img/no_in_progress_demand.svg";
  import { EventManager } from "@core/managers/event.manager";
  import { VISUAL_LOADING_DELAY } from "@core/constants";
  import { ETAB_EVENTS } from "@etab/config";
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import {
    GETTER_RECRUITMENTS_GET_IN_PROGRESS,
    ACTION_RECRUITMENTS_SET_SELECTED,
    GETTER_RECRUITMENTS_GET_SELECTED,
    GETTER_RECRUITMENTS_GET_SOFT_REMOVED,
    ACTION_RECRUITMENTS_GET_IN_PROGRESS,
  } from "@etab/stores/types";
  import { useSurveyStore } from "@etab/stores/survey";
  import { useRecruitmentsStore } from "@etab/stores/recruitments";
  import { GETTER_SURVEY_GET_AMOUNT } from "@etab/stores/types";
  import { ref, computed, onMounted } from "vue";
  import Demands from "@etab/components/dashboard/demand/DemandList.component.vue";
  import NoRecruitment from "@etab/components/dashboard/NoRecruitment.functional.vue";
  import SurveyTodoComponent from "@etab/components/dashboard/SurveyTodo.component.vue";

  const recruitmentsStore = useRecruitmentsStore();
  const surveyStore = useSurveyStore();

  let loadingTimeout;
  const loading = ref(true);
  const surveyTodo = ref(surveyStore[GETTER_SURVEY_GET_AMOUNT]);

  const recruitments = computed(() => {
    return (
      (recruitmentsStore[GETTER_RECRUITMENTS_GET_IN_PROGRESS] || [])
        // client side removal for speed purpose
        .filter((recruitment) => !removedRecruitments.value.includes(recruitment.getDemandId()))
    );
  });

  const removedRecruitments = computed(() => {
    return recruitmentsStore[GETTER_RECRUITMENTS_GET_SOFT_REMOVED];
  });

  const getRecruitments = function () {
    return recruitmentsStore[ACTION_RECRUITMENTS_GET_IN_PROGRESS]();
  };

  const loadRecruitments = async function (refresherDone) {
    const isFromPullToRefresh = typeof refresherDone === "function";
    try {
      startLoading();
      await getRecruitments();
    } catch (error) {
      NotificationsManager.contactEtabSupport();
      throw error;
    } finally {
      stopLoading();
      if (isFromPullToRefresh) {
        refresherDone();
      }
    }
  };

  const scrollToPreviouslyOpenedRecruitment = function () {
    const selectedRecruitmentId = recruitmentsStore[GETTER_RECRUITMENTS_GET_SELECTED];
    // get the selected recruitment (if any)
    if (selectedRecruitmentId) {
      const selectedEl = document.getElementById(selectedRecruitmentId);
      if (selectedEl) {
        // if we find one,we scroll to it
        ScrollManager.scrollToElement(selectedEl);
      }
      // we want this to be a one time access, in order to prevent weird behavior on navigation
      recruitmentsStore[ACTION_RECRUITMENTS_SET_SELECTED](undefined);
    } else {
      ScrollManager.scrollToTop();
    }
  };

  const goToCreation = function () {
    EventManager.emit(ETAB_EVENTS.RECRUITMENT.GO_TO_CREATION);
  };

  const startLoading = function () {
    clearTimeout(loadingTimeout);
    loading.value = true;
  };

  const stopLoading = function () {
    loadingTimeout = setTimeout(() => {
      loading.value = false;
    }, VISUAL_LOADING_DELAY);
  };

  onMounted(() => {
    scrollToPreviouslyOpenedRecruitment();
    loadRecruitments();
  });
</script>
